import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n, lifeAreaLabels } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import SubitemRow from "@/components/SubitemRow";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import {
  HeartPulse, Users, Home, Wallet, FileText, Shield, Monitor, ScrollText, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  "heart-pulse": HeartPulse,
  users: Users,
  home: Home,
  wallet: Wallet,
  "file-text": FileText,
  shield: Shield,
  monitor: Monitor,
  scroll: ScrollText,
  sparkles: Sparkles,
};

export default function LifeTreePage() {
  const { t, lang } = useI18n();
  const [areas, setAreas] = useState([]);
  const [subsByArea, setSubsByArea] = useState({});
  const [busy, setBusy] = useState(new Set());
  const labels = lifeAreaLabels[lang] || lifeAreaLabels.de;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/life-areas");
      setAreas(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSubs = async (areaId) => {
    if (subsByArea[areaId]) return;
    try {
      const { data } = await api.get(`/life-areas/${areaId}/subitems`);
      setSubsByArea((m) => ({ ...m, [areaId]: data }));
    } catch (e) { /* ignore */ }
  };

  const changeStatus = async (areaId, sub, nextStatus) => {
    setBusy((s) => new Set([...s, sub.id]));
    // optimistic
    setSubsByArea((m) => ({
      ...m,
      [areaId]: m[areaId].map((x) => (x.id === sub.id ? { ...x, status: nextStatus } : x)),
    }));
    try {
      const { data } = await api.patch(`/life-subitems/${sub.id}`, { status: nextStatus });
      // Update area progress live + notify other pages via custom event
      setAreas((arr) => arr.map((a) => (a.id === areaId ? { ...a, ...data.area } : a)));
      window.dispatchEvent(new CustomEvent("ag:trust-ring-changed", { detail: data.trust_ring }));
      window.dispatchEvent(new CustomEvent("ag:tasks-changed"));
      // Sanfte Rückmeldung, wenn LifeCore Aufgaben übernommen hat
      const resolved = data.tasks_delta?.auto_resolved_titles || [];
      if (resolved.length === 1) {
        toast(`🌿 "${resolved[0]}" — LifeCore hat erkannt, dass diese Aufgabe nicht mehr erforderlich ist.`);
      } else if (resolved.length > 1) {
        toast(`🌿 LifeCore hat ${resolved.length} Aufgaben für Sie übernommen.`);
      }
    } catch (e) {
      toast.error("Konnte Status nicht speichern");
      // rollback
      setSubsByArea((m) => ({
        ...m,
        [areaId]: m[areaId].map((x) => (x.id === sub.id ? { ...x, status: sub.status } : x)),
      }));
    } finally {
      setBusy((s) => {
        const next = new Set(s);
        next.delete(sub.id);
        return next;
      });
    }
  };

  return (
    <AppLayout>
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{t.nav.lifeTree}</div>
        <h1 className="font-serif text-5xl text-ag-ink mb-3">{t.lifeTree.title}</h1>
        <p className="text-slate-600 max-w-xl">{t.lifeTree.subtitle}</p>
      </header>

      <Accordion
        type="multiple"
        defaultValue={[]}
        className="space-y-3"
        data-testid="life-tree-accordion"
        onValueChange={(open) => open.forEach((cat) => {
          const a = areas.find((x) => x.category === cat);
          if (a) loadSubs(a.id);
        })}
      >
        {areas.map((a) => {
          const Icon = ICONS[a.icon] || Sparkles;
          const subs = subsByArea[a.id] || [];
          return (
            <AccordionItem
              key={a.id}
              value={a.category}
              id={a.category}
              className="ag-card border-0 px-5 md:px-7"
              data-testid={`life-tree-area-${a.category}`}
            >
              <AccordionTrigger className="hover:no-underline py-5">
                <div className="flex items-center gap-4 flex-1">
                  <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-ag-ink" strokeWidth={1.5} />
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-serif text-xl text-ag-ink">{labels[a.category] || a.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.statuses[a.status]}{typeof a.open_tasks === "number" && a.open_tasks > 0 ? ` · ${a.open_tasks} offen` : ""}</div>
                  </div>
                  <div className="hidden md:flex items-center gap-3 w-48 mr-4">
                    <Progress value={a.progress || 0} className="h-1.5" />
                    <span className="text-sm font-medium text-ag-ink tabular-nums w-10 text-right" data-testid={`area-progress-${a.category}`}>{a.progress}%</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pt-1">
                <div className="md:hidden mb-4 flex items-center gap-3">
                  <Progress value={a.progress || 0} className="h-1.5 flex-1" />
                  <span className="text-sm font-medium text-ag-ink tabular-nums">{a.progress}%</span>
                </div>
                <div className="text-xs text-slate-500 mb-3">
                  Tippen Sie auf einen Eintrag, um zwischen <strong>offen</strong>, <strong>erledigt</strong> und <strong>nicht relevant</strong> zu wechseln.
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {subs.length === 0 ? (
                    <div className="col-span-full text-sm text-slate-400 py-2">{t.common.loading}</div>
                  ) : subs.map((sub) => (
                    <SubitemRow
                      key={sub.id}
                      subitem={sub}
                      disabled={busy.has(sub.id)}
                      onChange={(next) => changeStatus(a.id, sub, next)}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </AppLayout>
  );
}
